import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';

export class WinNumbers extends PIXI.Sprite {
	public static ImageNumbers: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

	protected _resources: ResourcesLoader;

	protected _numbersContainer: PIXI.Sprite;

	protected _currencySign: string;
	protected _currencyISO: string;

	protected _symbolIDPrefix: string;
	protected _numberKerning: number = 0;
	protected _autoCenterMyself: boolean = true;
	protected _numberAnchor: Position;

	protected _tintValue: number;
	protected _shouldTintNumbers: boolean;

	protected _shouldAddShadow: boolean = false;
	protected _shadowTint: number = 0x000000;
	protected _shadowOffset: Position = {x: 0, y: 0.15};

	private _additionalOffset: number = 0;
	private _commaAdditionalOffset: number = 0;
	private _currencyOffset: Position;
	private _currencyIDPrefix: string;

	public static isValidCharacter(char: string): boolean {
		return typeof WinNumbers.ImageNumbers[char] !== 'undefined';
	}

	constructor(resources: ResourcesLoader, symbolIDPrefix: string, texture?: PIXI.Texture) {
		super(texture);

		this._resources = resources;
		this._symbolIDPrefix = symbolIDPrefix;

		this._numbersContainer = new PIXI.Sprite();
		this._numbersContainer.anchor.set(0.5, 0.5);
		this.numberAnchor = {x: 0, y: 0};

		this.addChild(this._numbersContainer);
	}

	public setParent(container: PIXI.Container): PIXI.Container {
		return super.setParent(container);
	}

	protected hasCurrency(numStr: string): boolean {
		if (this.currencyISO && this.currencySign) {
			return numStr.indexOf(this.currencySign) > -1;
		}

		return false;
	}

	protected splitCurrency(numStr: string): string {
		return numStr.replace(this.currencySign, '~');
	}

	public showNumber(numStr: string): void {
		this._numbersContainer.removeChildren();
		const hasCurrencySign = this.hasCurrency(numStr);

		if (hasCurrencySign) {
			numStr = this.splitCurrency(numStr);
		}

		let posOffset: number = 0;
		let prevIndividualNumber: string = '';

		for (const individualNum of numStr) {
			let numberSprite: PIXI.Sprite;

			if (individualNum === '.') {
				numberSprite = new PIXI.Sprite(this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}DOT.png`));
			} else if (individualNum === ',') {
				numberSprite = new PIXI.Sprite(this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}COMMA.png`));
			} else if (individualNum === 'x') {
				if (this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}X.png`)) {
					numberSprite = new PIXI.Sprite(this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}X.png`));
				} else {
					numberSprite = new PIXI.Sprite(this._resources.getSpritesheetTexture('NUMBERS.json', `GOLD_X.png`));
				}
			} else if (individualNum === '/') {
				numberSprite = new PIXI.Sprite(this._resources.getSpritesheetTexture('NUMBERS.json', `SMALL_BACKSLASH.png`));
			} else if (individualNum === '\'') {
				numberSprite = new PIXI.Sprite(
					this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}APOSTROPHE.png`),
				);
			} else if (hasCurrencySign && individualNum === '~') {
				const currencyPrefix: string = this._currencyIDPrefix ? this._currencyIDPrefix : this._symbolIDPrefix;
				if (this._resources.getCurrencySpritesheetTexture(this._currencyISO, `${currencyPrefix}CUR.png`)) {
					numberSprite = new PIXI.Sprite(this._resources.getCurrencySpritesheetTexture(this._currencyISO, `${currencyPrefix}CUR.png`));
				} else {
					numberSprite = new PIXI.Sprite(this._resources.getCurrencySpritesheetTexture(this._currencyISO, `LARGE_CUR.png`));
				}
			} else if (WinNumbers.isValidCharacter(individualNum)) {
				numberSprite = new PIXI.Sprite(
					this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}${individualNum}.png`),
				);
			} else {
				numberSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
				numberSprite.width = this._resources.getSpritesheetTexture('NUMBERS.json', `${this._symbolIDPrefix}DOT.png`).width;
			}

			numberSprite.position.set(posOffset, 0);
			this._numbersContainer.addChild(numberSprite);
			numberSprite.anchor.set(this.numberAnchor.x, this.numberAnchor.y);

			if (this._shouldTintNumbers) {
				numberSprite.tint = this.tintValue;
			}

			posOffset += numberSprite.width + this.numberKerning + this._additionalOffset;

			if (
				individualNum === '.' ||
				individualNum === ',' ||
				individualNum === '\'' ||
				prevIndividualNumber === '.' ||
				prevIndividualNumber === ',' ||
				prevIndividualNumber === '\''
			) {
				numberSprite.position.x += this._commaAdditionalOffset;
				posOffset += this._commaAdditionalOffset;
			}

			if (hasCurrencySign && individualNum === '~' && this._currencyOffset) {
				if (this._currencyOffset.x) {
					numberSprite.position.x += this._currencyOffset.x;
				}

				if (this._currencyOffset.y) {
					numberSprite.position.y += this._currencyOffset.y;
				}
			}

			if (this._shouldAddShadow) {
				const shadowSprite = new PIXI.Sprite(numberSprite.texture);
				shadowSprite.position.set(
					numberSprite.position.x + numberSprite.width * this._shadowOffset.x,
					numberSprite.position.y + numberSprite.height * this._shadowOffset.y,
				);
				shadowSprite.tint = this._shadowTint;
				this._numbersContainer.addChildAt(shadowSprite, 0);
				shadowSprite.anchor.set(numberSprite.anchor.x, numberSprite.anchor.y);
			}
			prevIndividualNumber = individualNum;
		}

		if (this.autoCenterMyself) {
			this.centerMyself(posOffset - this.numberKerning);
		}
	}

	protected centerMyself(width: number): void {
		this._numbersContainer.position.set(width / -2, (this.children[0] as PIXI.Sprite).height / -2);
	}

	protected centerHeight(height?: number): void {
		if (height) {
			this._numbersContainer.position.y = height / -2;
		}
	}

	protected centerWidth(width: number): void {
		if (width) {
			this._numbersContainer.position.y = width / -2;
		}
	}

	public set additionalOffset(value: number) {
		this._additionalOffset = value;
	}

	public set comaAdditionalOffset(value: number) {
		this._commaAdditionalOffset = value;
	}

	public set numberKerning(value: number) {
		this._numberKerning = value;
	}

	public get numberKerning(): number {
		return this._numberKerning;
	}

	public set autoCenterMyself(value: boolean) {
		this._autoCenterMyself = value;
	}

	public get autoCenterMyself(): boolean {
		return this._autoCenterMyself;
	}

	public set numberAnchor(value: Position) {
		this._numberAnchor = value;
	}

	public get numberAnchor(): Position {
		return this._numberAnchor;
	}

	public get currencySign(): string {
		// return this._currencyFormatter.currencySign;
		return this._currencySign;
	}

	public get currencyISO(): string {
		// return this._currencyFormatter.currency
		return this._currencyISO;
	}

	public set currencySign(currencySign: string) {
		this._currencySign = currencySign;
	}

	public set currencyISO(currencyISO: string) {
		this._currencyISO = currencyISO;
	}

	public get container(): PIXI.Sprite {
		return this._numbersContainer;
	}

	public get totalHeight(): number {
		return this.container.getBounds().height;
	}

	public get totalWidth(): number {
		return this.container.getBounds().width;
	}

	public set shouldTintNumbers(value: boolean) {
		this._shouldTintNumbers = value;
	}

	public set tintValue(value: number) {
		this._tintValue = value;
	}

	public get tintValue(): number {
		return this._tintValue;
	}

	public set shadowTint(tint: number) {
		this._shadowTint = tint;
	}

	public set shadowOffset(offset: Position) {
		this._shadowOffset = offset;
	}

	public set addShadow(shouldAdd: boolean) {
		this._shouldAddShadow = shouldAdd;
	}

	public set currencyOffset(value: Position) {
		this._currencyOffset = value;
	}

	public set currencyIDPrefix(value: string) {
		this._currencyIDPrefix = value;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/winpresentation/WinNumbers.ts